<template>
  <div class="nav-container left-nav">
    <!-- Toggle Checkbox -->
    <input
      type="checkbox"
      id="toggle-checkbox"
      class="toggle-checkbox"
    />
  
    <!-- Hamburger Icon -->
    <label
      for="toggle-checkbox"
      class="toggle-btn menu-icon"
      aria-label="Open Menu"
    >
      <LeftNavIcon />
    </label>
  
    <!-- Sidebar -->
    <nav class="sidebar">
      <div class="sidebar-content">
        <ul class="menu-list">
          <li class="project-li">
            <span class="section-header">MENU</span>
          </li>
          <li>
            <a href="#" @click.prevent="setPage('dashboard')" class="info-link">
              <HomeIcon />
              Home
            </a>  
          </li>
        </ul>

        <ul class="menu-list">
          <li class="project-li">
            <span class="section-header">PROJECTS</span>
          </li>
          <li
            @click.prevent.stop="projectClicked(project)"
            v-for="project in filteredProjects"
            :key="project.id"
            :class="getActiveProjectClass(project)"
            class="project-list-li"
          >
            <div class="project-color" :style="{ background: project.color ? project.color : 'var(--primary-color)' }"></div>
            <div class="d-flex gap-5 capatalize-text">
              {{ project.name }}
            </div>
            <!-- Dropdown Container -->
            <div class="dropdown-container" v-click-outside="closeDropdown">
              <button
                class="dropdown-toggle"
                @click.prevent.stop="toggleDropdown(project.id)"
                :aria-expanded="openDropdownId === project.id"
                aria-haspopup="true"
                :id="'dropdown-button-' + project.id"
              >
                <MenuIcon />
              </button>
  
              <!-- Dropdown Menu -->
              <ul
                v-if="openDropdownId === project.id"
                class="project-dropdown-menu"
                role="menu"
                :aria-labelledby="'dropdown-button-' + project.id"
              >
                <li role="menuitem">
                  <button @click.prevent="deleteProject(project)">
                    Delete
                  </button>
                  <button @click.prevent.stop="$store.dispatch('showProjectModal', {showModal: true, editingProject: {...project}})">
                    Edit
                  </button>
                </li>
              </ul>
            </div>
          </li>
          
          <li @click="$store.dispatch('showProjectModal', {showModal: true, editingProject: {color: `#48B587`}})" class="cursor-pointer gap-1-rem d-flex project-list-li" id="add-project-li">
            <PlusCircleIcon class="add-project-svg"  />
            Add Project
          </li>
        </ul>

        <ul class="menu-list">
          <li class="project-li">
            <span class="section-header">MAILBOXES</span>
          </li>
          <li
            @click.prevent.stop="inboxClicked(connection)"
            v-for="connection in filteredConnections"
            :key="connection.id"
            :class="getActiveProjectClass(connection)"
            class="project-list-li"
          >
            <GmailIcon />
            <div class="d-flex gap-5">
              {{ connection.account_name }}
            </div>
          </li>
          <li @click.prevent="setPage('connections')" class="cursor-pointer gap-1-rem d-flex project-list-li" id="add-email-li">
            <PlusCircleIcon class="add-project-svg"  />
            Add Email
          </li>
        </ul>

        <ul class="menu-list bottom">
          <li id="tutorial-button">
            <a href="#" @click.prevent="startTour()" class="bottom-nav-list-item">
              <HelpIcon />
              Tutorial
            </a>
          </li>
          <li id="upload-info-li">
            <a href="#" @click.prevent="setPage('data')" class="bottom-nav-list-item">
              <InfoIcon />
              Info
            </a>
          </li>
          <li id="connection-li">
            <a href="#" @click.prevent="setPage('connections')" class="bottom-nav-list-item">
              <ConnectIcon />
              Connections
            </a>
          </li>
          <li id="feedback-li">
            <a href="mailto:belbron@pairboard.com"  class="bottom-nav-list-item">
              <FeedbackIcon />
              Feedback
            </a>
          </li>
        
          <li>
            <a href="#" @click.prevent="handleLogout" class="bottom-nav-list-item">
              <LogoutIcon />
              Logout
            </a>
          </li>
        </ul>
      </div>
    </nav>

    <!-- Modal -->
    <EditProjectModal/>
  </div>
</template>
  
<script>
import { mapActions } from "vuex";
import LogoutIcon from "@/components/icons/LogoutIcon.vue";
import InfoIcon from "@/components/icons/InfoIcon.vue";
import LeftNavIcon from "@/components/icons/LeftNavIcon.vue";
import MenuIcon from "@/components/icons/MenuIcon.vue";
import PlusCircleIcon from "@/components/icons/PlusCircleIcon.vue";
import projectsService from "@/services/projectsService";
import ConnectIcon from "./icons/ConnectIcon.vue";
import HomeIcon from "./icons/HomeIcon.vue";
import FeedbackIcon from "./icons/FeedbackIcon.vue";
import HelpIcon from "./icons/HelpIcon.vue";
import GmailIcon from "./icons/GmailIcon.vue";
import { mapGetters } from 'vuex';
import EditProjectModal from "./EditProjectModal.vue";


export default {
  name: "LeftNav",
  emits: ["createProject", "projectSelected"],
  inject: ["startTour"],
  components: {
    LogoutIcon,
    InfoIcon,
    LeftNavIcon,
    MenuIcon,
    PlusCircleIcon,
    ConnectIcon, 
    HomeIcon,
    FeedbackIcon,
    HelpIcon,
    GmailIcon,
    EditProjectModal,
  },
  computed: {
    ...mapGetters(['projects','selectedProject','connections','showProjectModal']),
    filteredProjects() {
      return this.projects.filter((project) => !project.date_deleted);
    },
    filteredConnections() {
      // Filter connections based on category 'Email'
      return this.connections.filter(connection => connection.category === 'Email');
    },
  },
  data() {
    return {
      openDropdownId: null,
      editingProject: { color: "#48B587" },
      projectColors: [
        "#d63333", // Red
        "#FF6B6B", // Vivid Red
        "#FF9F43", // Bright Orange
        "#FFC107", // Bold Yellow
        "#28C76F", // Fresh Green
        "#48B587", // Emerald Green
        "#00CFE8", // Electric Cyan
        "#007BFF", // Strong Blue
        "#7367F0", // Royal Purple
        "#D63384", // Magenta Pink
      ],
    };
  },
  methods: {
    ...mapActions("auth", ["logout", "isAuthenticated"], "editProject"),
    inboxClicked(inbox) {
      this.$store.dispatch('setSelectedInbox', inbox);
      this.setPage('email');
    },
    getProjectColorClass(projectColor, color) {
      return color === projectColor ? "project-color-option-selected" : "project-color-option";
    },
    projectClicked(project) {
      this.$store.dispatch('selectProject', project);
      if (window.location.pathname !== '/dashboard') {
        this.setPage('dashboard');
      }
    },
    handleLogout() {
      this.logout();
      window.location.href = "/login";
    },
    getActiveProjectClass(project) {
      return {
        "project-list-li": true,
        "active-project":
          this.selectedProject &&
          project.id === this.selectedProject.id &&
          window.location.pathname === '/dashboard',
      };
    },
    toggleDropdown(id) {
      this.openDropdownId = this.openDropdownId === id ? null : id;
    },
    closeDropdown() {
      this.openDropdownId = null;
    },
    deleteProject(project) {
      const now = new Date();
      project.date_deleted = now.toISOString().slice(0, 19).replace("T", " ");
      projectsService.updateProject({
        id: project.id,
        date_deleted: project.date_deleted,
      });
    },
    editProjectClicked(project) {
      this.showProjectModal = true;
      this.editingProject = structuredClone(project);
    },
    setPage(page) {
      this.$router.push("/" + page);
    },
  },
};
</script>

<style scoped>
:root {
  --primary-color: #0049f5;
  --secondary-color: #0049f5;
  --text-color: #fff; 
  --bg-color: #000;  
}

.input-div {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

/* Color Circles */
.project-color-option,
.project-color-option-selected {
  width: calc(10% - 1rem);
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-sizing: border-box;
}
.project-color-option-selected {
  border: 2px solid var(--text-color);
}
.project-color-option-selected svg {
  stroke: var(--text-color);
}

/* Navigation Layout */
.bottom-nav-list-item {
  gap: 1rem;
  display: flex;
  color: var(--white-color) !important;
}
.project-color {
  width: 20px; 
  height: 20px;
  border-radius: 10px;
}

/* Section Header */
.section-header {
  font-weight: 500;
  font-size: 13px;
  text-transform: uppercase;
  color: var(--dark-color-6b);
}

/* Toggle / Menu Icons */
.toggle-checkbox {
  display: none;
}
.toggle-btn {
  font-size: 24px;
  color: var(--text-color);
  border: none;
  padding: 10px 15px;
  cursor: pointer;
  transition: background-color 0.2s;
  z-index: 1001;
}
.menu-icon {
  position: fixed;
  top: 15px; 
  left: 250px; /* Your original position */
  border-radius: 4px;
  transition: transform 0.3s ease;
}
.toggle-checkbox:checked + .menu-icon {
  transform: translateX(-250px);
}
.close-icon {
  position: absolute;
  top: 15px; 
  left: 15px;
  transform: translateX(-300px);
  transition: transform 0.3s ease;
}
.toggle-checkbox:checked ~ .sidebar .close-icon {
  transform: translateX(0);
}

/* Container for the entire nav + sidebar */
.nav-container {
  display: flex;
  transition: margin-left 0.3s ease;
  height: calc(100vh - 80px);
}

/* Sidebar (visible by default) */
.sidebar {
  position: fixed;
  width: 300px;
  background-color: var(--Theme-Color-Secondary-Black);
  color: var(--text-color);
  flex-shrink: 0;
  transform: translateX(0);
  transition: transform 0.3s ease;
  height: 95vh; /* your original setting */
}

/* If the checkbox is checked, hide the sidebar (desktop effect) */
.toggle-checkbox:checked ~ .sidebar {
  transform: translateX(-100%);
}

/* Sidebar content */
.sidebar-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0 20px 20px;
  box-sizing: border-box;
  height: 100%;
}

/* Menu List */
.menu-list {
  list-style: none;
  margin: 0;
  padding: 1rem 0;
}
.menu-list:not(:last-child):not(:nth-last-child(2))  {
  border-bottom: 1px solid var(--gray-color-2c);
}
.menu-list li {
  padding: 5px;
  border-radius: 5px;
  transition: background-color 0.2s;
  font-size: 15px;
  text-decoration: none;
}
.menu-list li:not(:has(a.logout-link)):hover {
  background-color: var(--gray-color-2c);
}
.menu-list.bottom{
  margin-top:auto;
}
.project-li {
  display: flex;
  justify-content: space-between;
  cursor: pointer;
}
.project-list-li {
  display: flex;
  gap: 1rem;
  align-items: center;
  cursor: pointer;
  color: var(--text-color);
}

/* Action Buttons, Etc. */
.add-project-svg {
  border-radius: 5px;
  cursor: pointer;
}
.logout-link,
.info-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: var(--text-color);
  font-size: 15px;
  gap: 1rem;
}
.logout-link:hover,
.info-link:hover {
  color: var(--dark-colo-a0);
}
.logout-icon {
  margin-right: 8px;
}

/* Project Dropdown */
.dropdown-container {
  position: relative;
  margin-left: auto;
}
.dropdown-toggle {
  display: none;
  background: none;
  border: none;
  color: var(--text-color);
  cursor: pointer;
  padding: 0;
}
.project-list-li:hover .dropdown-toggle {
  display: flex;
}
.project-dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: var(--gray-color-2c);
  border: 1px solid var(--gray-color-2c);
  border-radius: 5px;
  padding: 5px 0;
  list-style: none;
  margin: 0;
  min-width: 100px;
  z-index: 10;
}
.project-dropdown-menu li button {
  background: none;
  border: none;
  color: var(--text-color);
  width: 100%;
  text-align: left;
  cursor: pointer;
  padding: 5px;
}
.project-dropdown-menu li button:hover {
  background-color: var(--gray-color-2c);
}

/* Active Project */
.active-project {
  background-color: var(--gray-color-2c);
}

/* 
  Mobile Adjustments:
  1) Hide sidebar by default on small screens (translateX(-100%)).
  2) Checking the checkbox slides it in. 
  3) Reposition the hamburger icon so it’s not floating in the middle.
*/
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }
  .toggle-checkbox:checked ~ .sidebar {
    transform: translateX(0);
  }
  .menu-icon {
    top: 65px;  /* or 100px, etc. */
    left: 0px; /* keep some padding from the left edge */
    z-index: 2000; /* ensure it’s on top of other elements */
  }
  .close-icon {
    top: 65px;  /* or 100px, etc. */
    left: 0px; /* keep some padding from the left edge */
    z-index: 2000; /* ensure it’s on top of other elements */
  }
  .toggle-checkbox:checked + .menu-icon {
    transform: translateX(0px);
  }
  .sidebar-content {
    height: auto;
  }
}
</style>
